/**
 * PDF Preview Component
 * Displays PDF files with enhanced viewer features
 */

import React, { useState } from 'react';
import {
  FileText, Download, ExternalLink, ZoomIn, ZoomOut,
  ChevronLeft, ChevronRight, Maximize2, Info, Printer,
  Share2, Copy, Check
} from 'lucide-react';

interface PDFPreviewProps {
  asset: {
    id: number;
    file_name: string;
    file_path?: string;
    file_size?: number;
    r2_key?: string;
    metadata?: any;
  };
  apiBaseUrl?: string;
}

export default function PDFPreview({ asset, apiBaseUrl = '' }: PDFPreviewProps) {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Construct the PDF URL
  const getPdfUrl = () => {
    if (asset.r2_key) {
      return `${apiBaseUrl}/api/assets/${asset.id}/download`;
    }
    if (asset.file_path) {
      return asset.file_path.startsWith('http') ? asset.file_path : `${apiBaseUrl}${asset.file_path}`;
    }
    return '';
  };

  const pdfUrl = getPdfUrl();

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 25, 50));
  };

  const handlePrint = () => {
    const iframe = document.querySelector('.pdf-iframe') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.print();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = asset.file_name || 'document.pdf';
    link.click();
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(pdfUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleFullscreen = () => {
    const container = document.querySelector('.pdf-preview-container');
    if (container) {
      if (!isFullscreen) {
        container.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
      setIsFullscreen(!isFullscreen);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 pdf-preview-container">
      {/* Header Toolbar */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-5 h-5 text-red-600" />
            <div>
              <h2 className="text-lg font-semibold">{asset.file_name || 'PDF Document'}</h2>
              <p className="text-xs text-gray-500">{formatFileSize(asset.file_size)}</p>
            </div>
          </div>

          {/* Toolbar Actions */}
          <div className="flex items-center space-x-2">
            {/* Zoom Controls */}
            <div className="flex items-center space-x-1 border-r pr-2">
              <button
                onClick={handleZoomOut}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Zoom Out"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium px-2 min-w-[60px] text-center">
                {zoom}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Zoom In"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Page Navigation */}
            <div className="flex items-center space-x-1 border-r pr-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="p-2 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-1">
              <button
                onClick={handlePrint}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Print"
              >
                <Printer className="w-4 h-4" />
              </button>
              <button
                onClick={handleDownload}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={handleCopyLink}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Copy Link"
              >
                {copySuccess ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={() => window.open(pdfUrl, '_blank')}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Open in New Tab"
              >
                <ExternalLink className="w-4 h-4" />
              </button>
              <button
                onClick={handleFullscreen}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Fullscreen"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 relative bg-gray-100">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <div className="text-center">
              <div className="inline-flex items-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                <span className="text-gray-600">Loading PDF...</span>
              </div>
            </div>
          </div>
        )}

        {pdfUrl ? (
          <iframe
            src={`${pdfUrl}#page=${currentPage}&zoom=${zoom}`}
            className="pdf-iframe w-full h-full border-0"
            title={asset.file_name || 'PDF Document'}
            onLoad={() => setIsLoading(false)}
            style={{
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              width: `${100 / (zoom / 100)}%`,
              height: `${100 / (zoom / 100)}%`
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">PDF Preview Not Available</p>
              <p className="text-sm text-gray-500 mt-2">
                The PDF file path is not configured correctly
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info Bar */}
      <div className="bg-white border-t px-4 py-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span className="flex items-center space-x-1 text-gray-600">
              <Info className="w-4 h-4" />
              <span>PDF Document</span>
            </span>
            {asset.metadata?.pageCount && (
              <span className="text-gray-600">
                {asset.metadata.pageCount} pages
              </span>
            )}
            {asset.metadata?.author && (
              <span className="text-gray-600">
                Author: {asset.metadata.author}
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {asset.metadata?.created && (
              <span>Created: {new Date(asset.metadata.created).toLocaleDateString()}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}